import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { useBoolVariation } from "@launchdarkly/react-sdk";
import { Amplify } from "aws-amplify";
import {
  autoSignIn,
  confirmSignIn,
  confirmSignUp,
  getCurrentUser,
  resendSignUpCode,
  signIn,
  signOut as amplifySignOut,
  signUp,
  type AuthUser,
} from "aws-amplify/auth";
import {
  Button,
  Callout,
  Card,
  Container,
  Flex,
  Heading,
  Text,
  TextField,
} from "@radix-ui/themes";
import { authApi } from "../api";
import { useAmplitude } from "./AmplitudeProvider";

/**
 * Configure Amplify Auth against the Cognito user pool.
 *
 * Values are sourced from `VITE_PUBLIC_*` environment variables so they are
 * available to the client bundle. See `.env.local` for the expected keys.
 *
 * The user pool uses passwordless SMS OTP as the first auth factor, so the
 * login UI only collects a phone number and the one-time code delivered via
 * SMS.
 */
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_PUBLIC_COGNITO_USER_POOL_ID,
      userPoolClientId: import.meta.env.VITE_PUBLIC_COGNITO_CLIENT_ID,
      loginWith: {
        phone: true,
      },
    },
  },
});

type AuthContextValue = {
  /** The signed-in Cognito user, or `null` when unauthenticated. */
  user: AuthUser | null;
  /** `true` once the initial session check has completed. */
  ready: boolean;
  /** Signs the current user out and clears local auth state. */
  signOut: () => Promise<void>;
  /** Re-reads the current session from Cognito. */
  refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// Query params for magic-link sign-in, e.g.
// https://chatwithiris.com/exp/flourishing/v1?u=<username>&ml=<token>. The
// Iris messaging tool generates these links; `token` is a short-lived,
// single-use, HMAC-signed CUSTOM_AUTH challenge answer (see
// MagicLink*AuthChallenge triggers in lib/users/cognito-stack.ts).
const MAGIC_LINK_USERNAME_PARAM = "u";
const MAGIC_LINK_TOKEN_PARAM = "ml";

/**
 * Consumes a magic-link username/token pair from the current URL, if
 * present, by running Cognito's CUSTOM_AUTH challenge. Both params are
 * stripped from the URL before the exchange is attempted (whether or not it
 * succeeds), so a token never lingers in browser history/bookmarks or leaks
 * via a Referer header to another origin.
 *
 * Failures (expired/invalid/already-used token) are swallowed: the caller
 * falls through to the normal interactive sign-in UI in that case.
 */
async function consumeMagicLinkFromUrl(): Promise<void> {
  if (typeof window === "undefined") {
    return;
  }

  const url = new URL(window.location.href);
  const username = url.searchParams.get(MAGIC_LINK_USERNAME_PARAM);
  const token = url.searchParams.get(MAGIC_LINK_TOKEN_PARAM);
  if (!username || !token) {
    return;
  }

  url.searchParams.delete(MAGIC_LINK_USERNAME_PARAM);
  url.searchParams.delete(MAGIC_LINK_TOKEN_PARAM);
  window.history.replaceState(null, "", url.toString());

  try {
    // Check if there's an existing session
    let currentUser: AuthUser | null = null;
    try {
      currentUser = await getCurrentUser();
    } catch {
      // No existing session, which is fine
    }

    // If a user is already signed in and it's not the target user, sign them out
    if (currentUser && currentUser.username !== username) {
      await amplifySignOut();
      currentUser = null;
    }

    const { nextStep } = await signIn({
      username,
      options: { authFlowType: "CUSTOM_WITHOUT_SRP" },
    });
    if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE") {
      await confirmSignIn({ challengeResponse: token });
    }
  } catch (err) {
    console.error("Magic-link sign-in failed:", err);
  }
}

/**
 * Reads Cognito auth state. Must be called inside a `CognitoAuthProvider`.
 */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within a CognitoAuthProvider");
  }
  return ctx;
}

/**
 * Provides Cognito-backed auth state to a subtree. This is intentionally scoped
 * to the routes that require authentication rather than the whole app, since
 * every other page is unauthenticated (and prerendered).
 */
export function CognitoAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [ready, setReady] = useState(false);
  const amplitudeClient = useAmplitude();

  const refresh = useCallback(async () => {
    try {
      setUser(await getCurrentUser());
    } catch {
      setUser(null);
    }
  }, []);

  const signOut = useCallback(async () => {
    await amplifySignOut();
    setUser(null);
  }, []);

  useEffect(() => {
    void (async () => {
      // Try the magic link first - it's the primary sign-in path - then
      // fall back to whatever session Cognito already has (or none, in
      // which case RequireAuth shows the interactive sign-in UI).
      await consumeMagicLinkFromUrl();
      await refresh();
      setReady(true);
    })();
  }, [refresh]);

  // Identify the signed-in user to Amplitude so events on authenticated
  // pages are attributed to their account, not just their device. Clears
  // the association on sign-out (user becomes null).
  useEffect(() => {
    amplitudeClient?.setUserId(user?.userId);
  }, [amplitudeClient, user]);

  const value = useMemo(
    () => ({ user, ready, signOut, refresh }),
    [user, ready, signOut, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Gate that ensures the user is authenticated before rendering `children`.
 * Unauthenticated users are shown a passwordless sign-in UI (phone number +
 * SMS one-time code). Must be rendered inside a `CognitoAuthProvider`.
 *
 * `intent="waitlist"` swaps in waitlist-specific copy ("Join the Waitlist"
 * instead of "Sign in"/"Create account", etc.) for routes where visitors are
 * never signing in to an existing account - they're joining the waitlist.
 * Omit it (the default) for the normal sign-in-or-create-account experience.
 */
export function RequireAuth({
  children,
  intent,
  onAuthenticated,
}: {
  children: ReactNode;
  intent?: "waitlist";
  /**
   * Fires once the user authenticates, reporting whether they signed in to an
   * existing account (`"signIn"`) or just created a brand-new one (`"signUp"`).
   * Lets callers branch on freshly-created accounts - e.g. the waitlist modal
   * skips its status API check for new signups, which are always "waiting" and
   * whose provisioning (which writes waitlist_status) hasn't run yet.
   */
  onAuthenticated?: (mode: "signIn" | "signUp") => void;
}) {
  const { user, ready, refresh } = useAuth();

  if (!ready) {
    return null;
  }

  if (!user) {
    return (
      <SmsOtpSignIn
        onSignedIn={async (mode) => {
          onAuthenticated?.(mode);
          await refresh();
        }}
        intent={intent}
      />
    );
  }

  return <>{children}</>;
}

/**
 * Normalizes user-entered phone input into E.164 form. US-centric: a bare
 * 10-digit number is prefixed with `+1`, an 11-digit number starting with `1`
 * gets a leading `+`, and anything already starting with `+` is preserved.
 */
function toE164(input: string): string {
  const trimmed = input.trim();
  if (trimmed.startsWith("+")) {
    return "+" + trimmed.slice(1).replace(/\D/g, "");
  }
  const digits = trimmed.replace(/\D/g, "");
  if (digits.length === 10) {
    return "+1" + digits;
  }
  if (digits.length === 11 && digits.startsWith("1")) {
    return "+" + digits;
  }
  return "+" + digits;
}

/**
 * Passwordless sign-in form using Cognito's `USER_AUTH` flow with the
 * `SMS_OTP` challenge. Collects a phone number, then the one-time code.
 */
function SmsOtpSignIn({
  onSignedIn,
  intent,
}: {
  onSignedIn: (mode: "signIn" | "signUp") => Promise<void>;
  intent?: "waitlist";
}) {
  const navigate = useNavigate();
  const isWaitlist = intent === "waitlist";
  // Brand-new signups are routed to a dedicated landing page after account
  // creation completes: the waitlist explanation page when the flag is on,
  // otherwise /welcome. See handleAccountCreated below. Existing users
  // signing in are left on whatever page they originally requested.
  const waitlistEnabled = useBoolVariation("iris-cta-waitlist-enabled", false);
  const [step, setStep] = useState<"phone" | "email" | "code">("phone");
  // Tracks whether the code step is completing a sign-in or a brand new
  // account's sign-up, since confirming the SMS code uses different APIs.
  const [mode, setMode] = useState<"signIn" | "signUp">("signIn");
  // Which OTP channel is active for the in-progress sign-in attempt. Sign-up
  // only ever happens via phone, but sign-in can fall back to email for
  // accounts with an invalid/undeliverable phone number on file.
  const [channel, setChannel] = useState<"SMS_OTP" | "EMAIL_OTP">("SMS_OTP");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  // Set when sign-in fails because no account exists for the entered number,
  // so we can offer to create one instead of just showing a dead-end error.
  const [accountNotFound, setAccountNotFound] = useState(false);
  // Set when Cognito rejects the phone number itself (malformed or unable
  // to deliver an SMS), so we can offer email sign-in as a recovery path.
  const [phoneInvalid, setPhoneInvalid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  // The Cognito username actually used for the in-progress sign-in attempt.
  // Usually the entered phone number or email, but may be a resolved
  // Cognito sub when the phone number exists but was never verified (see
  // attemptSignIn).
  const [signInUsername, setSignInUsername] = useState("");

  // Called when a brand-new account finishes confirmation (as opposed to an
  // existing user signing in). Refreshes auth state, then routes to the
  // appropriate landing surface. Waitlist signups happen inside a modal with
  // no dedicated route: refreshing auth state makes RequireAuth swap in the
  // waitlist confirmation in place, so there's nothing to navigate to.
  const handleAccountCreated = async () => {
    await onSignedIn("signUp");
    if (isWaitlist) {
      return;
    }
    navigate("/welcome");
  };

  const submitPhone = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setAccountNotFound(false);
    setPhoneInvalid(false);
    setSubmitting(true);
    try {
      await attemptSignIn(toE164(phone), "SMS_OTP");
    } catch (err) {
      if (err instanceof Error && err.name === "UserNotFoundException") {
        // Cognito's alias resolution for USERNAME in InitiateAuth is
        // inconsistent depending on the user's other attributes:
        //   - Phone-only accounts: an unverified phone_number still
        //     resolves fine as USERNAME.
        //   - Accounts with BOTH email and phone_number: an unverified
        //     phone_number does NOT resolve as USERNAME and Cognito throws
        //     UserNotFoundException, even though the account genuinely
        //     exists (e.g. a subscriber who purchased outside the product,
        //     has an email on file, and never messaged in to verify their
        //     phone). Confirmed via manual testing - CONFIRM_SIGN_UP
        //     (unconfirmed account) is reported correctly by Cognito/Amplify
        //     in both cases and doesn't need this fallback; only this
        //     specific verified-account/unverified-phone/has-email
        //     combination requires resolving the real username server-side
        //     before assuming no account exists.
        const resolved = await authApi.resolvePhoneUsername(toE164(phone));
        if (resolved.success) {
          try {
            await attemptSignIn(resolved.data.username, "SMS_OTP");
          } catch (retryErr) {
            setError(
              retryErr instanceof Error
                ? retryErr.message
                : "Could not send a code.",
            );
          }
        } else if (isWaitlist) {
          // The waitlist flow has no separate "confirm you want to join"
          // screen - the phone form above already carries the terms/privacy
          // agreement, so an unrecognized number goes straight into account
          // creation (and from there, the same OTP step used for sign-in).
          await createAccount();
        } else {
          setAccountNotFound(true);
        }
      } else if (
        err instanceof Error &&
        (err.name === "InvalidParameterException" ||
          err.name === "CodeDeliveryFailureException")
      ) {
        // Cognito rejected the phone number itself (malformed, or unable to
        // deliver an SMS to it). Offer email sign-in instead of a dead-end
        // error, for accounts that have an email on file.
        setPhoneInvalid(true);
      } else {
        setError(err instanceof Error ? err.message : "Could not send a code.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submitEmail = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await attemptSignIn(email.trim(), "EMAIL_OTP");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not send a code.");
    } finally {
      setSubmitting(false);
    }
  };

  // Initiates USER_AUTH sign-in with the given OTP challenge for the given
  // username (a phone number or email alias, or a resolved Cognito username)
  // and advances local state based on the resulting sign-in step.
  const attemptSignIn = async (
    username: string,
    preferredChallenge: "SMS_OTP" | "EMAIL_OTP",
  ) => {
    setSignInUsername(username);
    setChannel(preferredChallenge);
    let { nextStep } = await signIn({
      username,
      options: {
        authFlowType: "USER_AUTH",
        preferredChallenge,
      },
    });

    // When the pool allows multiple first auth factors (e.g. password and
    // SMS OTP), Cognito asks us to choose one before issuing the challenge.
    // Select the preferred challenge to continue the passwordless flow.
    if (
      nextStep.signInStep === "CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION"
    ) {
      ({ nextStep } = await confirmSignIn({
        challengeResponse: preferredChallenge,
      }));
    }

    if (
      nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_SMS_CODE" ||
      nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_EMAIL_CODE"
    ) {
      setMode("signIn");
      setStep("code");
    } else if (nextStep.signInStep === "DONE") {
      await onSignedIn("signIn");
    } else if (nextStep.signInStep === "CONFIRM_SIGN_UP") {
      // The account was created (e.g. a previous attempt) but the user
      // never entered the verification code. Resend a fresh code and drop
      // them back into the sign-up confirmation flow instead of erroring.
      await resendSignUpCode({ username });
      setMode("signUp");
      setStep("code");
    } else {
      setError(`Unexpected sign-in step: ${nextStep.signInStep}`);
    }
  };

  // Starts account creation for a phone number that isn't registered yet.
  // Cognito verifies the phone number via the same SMS one-time code used
  // for sign-in, so the code step below is shared between both flows.
  const createAccount = async () => {
    setError(null);
    setCreatingAccount(true);
    try {
      const normalizedPhone = toE164(phone);
      const { nextStep } = await signUp({
        username: normalizedPhone,
        options: {
          userAttributes: {
            phone_number: normalizedPhone,
            // Records acceptance of both the Terms of Service and Privacy
            // Policy (accepted implicitly by creating an account - see the
            // footnote below the create/join button) as a single ISO
            // timestamp. There's no separate custom:ack_privacy write -
            // the backend maps this one attribute to UserTable's
            // agreed_to_terms field.
            "custom:ack_terms": new Date().toISOString(),
            // Tags the account's origin so the provisioning lambda can
            // relay it to UserTable's user_source_id (and, for waitlist
            // signups, also set waitlist_status="waiting" so admin
            // announcements can target them).
            "custom:user_source_id": waitlistEnabled
              ? "waitlist"
              : "self-signup",
          },
          // Passing `true` here (instead of a SignInOptions object) makes
          // Amplify's post-confirmation auto sign-in default to the SRP auth
          // flow, which requires a password - it fails with a confusing
          // "password required to signIn" error for this passwordless-only
          // pool (even though the account was already confirmed
          // successfully). Explicitly requesting USER_AUTH + SMS_OTP here
          // makes autoSignIn use the same passwordless challenge as the
          // rest of this flow.
          autoSignIn: {
            authFlowType: "USER_AUTH",
            preferredChallenge: "SMS_OTP",
          },
        },
      });

      if (nextStep.signUpStep === "CONFIRM_SIGN_UP") {
        setMode("signUp");
        setAccountNotFound(false);
        setStep("code");
      } else if (nextStep.signUpStep === "DONE") {
        await handleAccountCreated();
      } else {
        setError(`Unexpected sign-up step: ${nextStep.signUpStep}`);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not create an account.",
      );
    } finally {
      setCreatingAccount(false);
    }
  };

  // Resends the OTP code for whichever flow is currently active. Sign-up
  // confirmation has a dedicated resend API; sign-in's OTP challenge is
  // instead resent by simply re-initiating the sign-in flow.
  const resendCode = async () => {
    setError(null);
    setResendSuccess(false);
    setResending(true);
    try {
      if (mode === "signUp") {
        await resendSignUpCode({ username: signInUsername || toE164(phone) });
      } else {
        let { nextStep } = await signIn({
          username: signInUsername,
          options: {
            authFlowType: "USER_AUTH",
            preferredChallenge: channel,
          },
        });
        if (
          nextStep.signInStep === "CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION"
        ) {
          ({ nextStep } = await confirmSignIn({
            challengeResponse: channel,
          }));
        }
        if (
          nextStep.signInStep !== "CONFIRM_SIGN_IN_WITH_SMS_CODE" &&
          nextStep.signInStep !== "CONFIRM_SIGN_IN_WITH_EMAIL_CODE"
        ) {
          setError(`Unexpected sign-in step: ${nextStep.signInStep}`);
          return;
        }
      }
      setResendSuccess(true);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not resend the code.",
      );
    } finally {
      setResending(false);
    }
  };

  const submitCode = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setResendSuccess(false);
    setSubmitting(true);
    try {
      if (mode === "signUp") {
        const { nextStep } = await confirmSignUp({
          username: toE164(phone),
          confirmationCode: code.trim(),
        });
        if (nextStep.signUpStep === "COMPLETE_AUTO_SIGN_IN") {
          const { nextStep: signInNextStep } = await autoSignIn();
          if (signInNextStep.signInStep === "DONE") {
            await handleAccountCreated();
          } else {
            setError("Unexpected confirmation step. Please try again.");
          }
        } else if (nextStep.signUpStep === "DONE") {
          await handleAccountCreated();
        } else {
          setError("Unexpected confirmation step. Please try again.");
        }
      } else {
        const { nextStep } = await confirmSignIn({
          challengeResponse: code.trim(),
        });
        if (nextStep.signInStep === "DONE") {
          await onSignedIn("signIn");
        } else {
          setError("Unexpected confirmation step. Please try again.");
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid code.");
    } finally {
      setSubmitting(false);
    }
  };

  const content = (
    <Flex direction="column" gap="4" p="2">
      <Heading size="5">
        {step === "code" && mode === "signUp"
          ? isWaitlist
            ? "Verify Your Number"
            : "Create account"
          : step === "phone" && accountNotFound
            ? isWaitlist
              ? "Join the Waitlist"
              : "Create account"
            : isWaitlist
              ? "Join the Waitlist"
              : "Sign in"}
      </Heading>
      {step === "phone" ? (
        accountNotFound ? (
          <Flex direction="column" gap="3">
            <Text size="2" color="gray">
              {isWaitlist
                ? `We'll text a one-time code to ${toE164(phone)} to confirm it's you.`
                : `We couldn't find an account for ${toE164(phone)}. We'll text a one-time code to confirm it's you.`}
            </Text>
            {error && (
              <Callout.Root color="red" size="1">
                <Callout.Text>{error}</Callout.Text>
              </Callout.Root>
            )}
            <Button
              type="button"
              size="3"
              loading={creatingAccount}
              disabled={creatingAccount}
              onClick={createAccount}
            >
              {isWaitlist ? "Join Waitlist" : "Create account"}
            </Button>
            <Text size="1" color="gray" align="center">
              {isWaitlist ? (
                <>
                  By joining, you agree to the{" "}
                  <a href="/terms-conditions" target="_blank" rel="noreferrer">
                    Terms
                  </a>{" "}
                  &amp;{" "}
                  <a href="/privacy" target="_blank" rel="noreferrer">
                    Privacy
                  </a>
                </>
              ) : (
                <>
                  By creating an account, you agree to our{" "}
                  <a href="/terms-conditions" target="_blank" rel="noreferrer">
                    Terms of Service
                  </a>{" "}
                  and{" "}
                  <a href="/privacy" target="_blank" rel="noreferrer">
                    Privacy Policy
                  </a>
                  .
                </>
              )}
            </Text>
            <Button
              type="button"
              variant="ghost"
              color="gray"
              size="2"
              onClick={() => {
                setAccountNotFound(false);
                setError(null);
              }}
            >
              Use a different number
            </Button>
          </Flex>
        ) : (
          <form onSubmit={submitPhone}>
            <Flex direction="column" gap="3">
              <Text size="2" color="gray">
                {isWaitlist
                  ? "Enter your phone number to join the waitlist. We'll text you a one-time code to confirm it's you."
                  : "Enter your phone number and we'll text you a one-time code."}
              </Text>
              <TextField.Root
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                aria-label="Phone number"
                placeholder="+1 555 123 4567"
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value);
                  setAccountNotFound(false);
                  setPhoneInvalid(false);
                }}
                required
                size="3"
              />
              {error && (
                <Callout.Root color="red" size="1">
                  <Callout.Text>{error}</Callout.Text>
                </Callout.Root>
              )}
              {phoneInvalid && (
                <Callout.Root color="amber" size="1">
                  <Flex direction="column" gap="2">
                    <Callout.Text>
                      We couldn&apos;t send a code to that phone number.
                    </Callout.Text>
                    {!isWaitlist && (
                      <Button
                        type="button"
                        variant="soft"
                        size="2"
                        onClick={() => {
                          setPhoneInvalid(false);
                          setError(null);
                          setStep("email");
                        }}
                      >
                        Sign in with email instead
                      </Button>
                    )}
                  </Flex>
                </Callout.Root>
              )}
              <Button
                type="submit"
                size="3"
                disabled={submitting || phone.trim().length === 0}
              >
                {submitting
                  ? "Sending…"
                  : isWaitlist
                    ? "Continue"
                    : "Send code"}
              </Button>
              {isWaitlist && (
                <Text size="1" color="gray" align="center">
                  By joining, you agree to the{" "}
                  <a href="/terms-conditions" target="_blank" rel="noreferrer">
                    Terms
                  </a>{" "}
                  &amp;{" "}
                  <a href="/privacy" target="_blank" rel="noreferrer">
                    Privacy
                  </a>
                </Text>
              )}
              {!isWaitlist && (
                <Button
                  type="button"
                  variant="ghost"
                  color="gray"
                  size="2"
                  onClick={() => {
                    setError(null);
                    setAccountNotFound(false);
                    setPhoneInvalid(false);
                    setStep("email");
                  }}
                >
                  Sign in with email instead
                </Button>
              )}
            </Flex>
          </form>
        )
      ) : step === "email" ? (
        <form onSubmit={submitEmail}>
          <Flex direction="column" gap="3">
            <Text size="2" color="gray">
              Enter your email address and we&apos;ll send you a one-time code.
            </Text>
            <TextField.Root
              type="email"
              inputMode="email"
              autoComplete="email"
              aria-label="Email address"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              size="3"
            />
            {error && (
              <Callout.Root color="red" size="1">
                <Callout.Text>{error}</Callout.Text>
              </Callout.Root>
            )}
            <Button
              type="submit"
              size="3"
              disabled={submitting || email.trim().length === 0}
            >
              {submitting ? "Sending…" : "Send code"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              color="gray"
              size="2"
              onClick={() => {
                setError(null);
                setStep("phone");
              }}
            >
              Use my phone number instead
            </Button>
          </Flex>
        </form>
      ) : (
        <form onSubmit={submitCode}>
          <Flex direction="column" gap="3">
            <Text size="2" color="gray">
              {mode === "signUp"
                ? `We just texted a code to ${toE164(phone)} to verify your number.`
                : channel === "EMAIL_OTP"
                  ? `Enter the code we sent to ${email.trim()}.`
                  : `Enter the code we sent to ${toE164(phone)}.`}
            </Text>
            <TextField.Root
              inputMode="numeric"
              autoComplete="one-time-code"
              aria-label="Verification code"
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              size="3"
            />
            {error && (
              <Callout.Root color="red" size="1">
                <Callout.Text>{error}</Callout.Text>
              </Callout.Root>
            )}
            {resendSuccess && !error && (
              <Callout.Root color="green" size="1">
                <Callout.Text>A new code has been sent.</Callout.Text>
              </Callout.Root>
            )}
            <Button
              type="submit"
              size="3"
              disabled={submitting || code.trim().length === 0}
            >
              {submitting ? "Verifying…" : "Verify"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="2"
              loading={resending}
              disabled={resending}
              onClick={resendCode}
            >
              Resend code
            </Button>
            <Button
              type="button"
              variant="ghost"
              color="gray"
              size="2"
              onClick={() => {
                setMode("signIn");
                setStep(
                  mode === "signUp" || channel === "SMS_OTP"
                    ? "phone"
                    : "email",
                );
                setCode("");
                setError(null);
                setAccountNotFound(false);
                setPhoneInvalid(false);
                setResendSuccess(false);
              }}
            >
              {mode === "signUp" || channel === "SMS_OTP"
                ? "Use a different number"
                : "Use a different email"}
            </Button>
          </Flex>
        </form>
      )}
    </Flex>
  );

  // Inside the waitlist modal the dialog supplies the surface (Theme,
  // padding, close button), so render the bare form. Standalone routes keep
  // the full-page container + card chrome.
  if (isWaitlist) {
    return content;
  }

  return (
    <Container size="1" className="route-page" maxWidth="420px">
      <Card>{content}</Card>
    </Container>
  );
}
